import { LostUnits } from "@deliverr/commons-clients";
import { defaultTheme as theme } from "@deliverr/ui/lib-esm/shared/theme";
import styled from "@emotion/styled";
import React from "react";
import { LoadingSpinner } from "./LoadingSpinner";
import { Button } from "@deliverr/ui/lib-esm/Button";
import { InputHTMLAttributes } from "react";
import { DetailedHTMLProps } from "react";
import ReactPaginate from "react-paginate";

export type ColumnDefinition = {
  heading: string;
  width: number;
  field: string;
  urlBuilder?: string;
  action?: any;
  transform?: any;
};

export type ColumnsDefinition = {
  columns: ColumnDefinition[];
};

const useWidth: number = 85;

const StyledRow = styled.tr`
  color: black;
  font-family: ${theme.font.family.STANDARD};
  font-size: ${theme.font.size.F1};
`;

const StyledTable = styled.table`
  table-layout: fixed;
  width: ${useWidth}vw;
  overflow: auto;
`;

// wanted to use --${theme.colors.BLUE[400]}; but they are too dark
const StyledHeader = styled.thead`
  display: block;
  width: ${useWidth}vw;
  background-color: LightBlue;
`;
const StyledBody = styled.tbody`
  display: inline-block;
  overflow-y: scroll;
  height: 60vh;
  width: ${useWidth}vw;
  vertical-align: top;
`;

const Th = styled.th`
  text-align: left;
`;

const Td = styled.td`
  text-overflow: ellipsis;
  overflow: hidden;
`;

const calcCellWidth = (width: number, totalWidth: number) =>
  Math.floor((width * useWidth) / (totalWidth + 10));

export type FixedWidthProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  column: ColumnDefinition;
  totalWidth: number;
};

export const FixedWidthCell = React.forwardRef<
  HTMLInputElement,
  FixedWidthProps
>(
  (
    { column, totalWidth, children, ...inputProps }: FixedWidthProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const width: number = calcCellWidth(column.width, totalWidth);

    return (
      <span style={{ width: `${width}vw`, maxWidth: `${width}vw` }}>
        <Td style={{ width: `${width}vw`, maxWidth: `${width}vw` }}>
          {children}
        </Td>
      </span>
    );
  }
);

const TableHeaderCell: React.FC<{
  column: ColumnDefinition;
  totalWidth: number;
}> = ({ column, totalWidth }) => {
  return (
    <span style={{ width: `${calcCellWidth(column.width, totalWidth)}vw` }}>
      <Th
        scope="col"
        style={{ width: `${calcCellWidth(column.width, totalWidth)}vw` }}
      >
        {column.action ? "" : column.heading}
      </Th>
    </span>
  );
};

const TableDataCell: React.FC<{
  column: ColumnDefinition;
  lostUnits: LostUnits;
  totalWidth: number;
}> = ({ column, lostUnits, totalWidth }) => {
  let value = (lostUnits as any)[column.field];

  if (column.transform) {
    value = column.transform(value);
  }

  if (column.urlBuilder && value) {
    const url = column.urlBuilder.replace("${link}", value);
    return (
      <FixedWidthCell column={column} totalWidth={totalWidth}>
        <a href={url} target="_blank" rel="noreferrer">
          {value}
        </a>
      </FixedWidthCell>
    );
  }

  if (column.action) {
    return (
      <FixedWidthCell column={column} totalWidth={totalWidth}>
        <button onClick={() => column.action(value)}>{column.heading}</button>
      </FixedWidthCell>
    );
  }

  return (
    <FixedWidthCell column={column} totalWidth={totalWidth}>
      {value}
    </FixedWidthCell>
  );
};

const TableRow: React.FC<{
  columns: ColumnsDefinition;
  lostUnits: LostUnits;
  totalWidth: number;
}> = ({ columns, lostUnits, totalWidth }) => {
  return (
    <StyledRow key={lostUnits.id}>
      {columns.columns.map((column: ColumnDefinition) => (
        <TableDataCell
          column={column}
          lostUnits={lostUnits}
          totalWidth={totalWidth}
        />
      ))}
    </StyledRow>
  );
};

export const TableList: React.FC<{
  columns: ColumnsDefinition;
  data: [];
  enablePagination: boolean;
  selectedPageIndex?: number;
  totalPages?: number;
  handlePageClick?: any;
}> = ({
  columns,
  data,
  enablePagination,
  selectedPageIndex,
  totalPages,
  handlePageClick,
}) => {
  if (data === undefined) {
    return <div>Initializing</div>;
  }

  if (data.length === 0) {
    return <LoadingSpinner loading={true} size="2x" />;
  }

  const totalWidth = columns.columns
    .map((record: ColumnDefinition) => record.width)
    .reduce((balance: number, value: number) => balance + value);

  if (enablePagination && selectedPageIndex && totalPages) {
    return (
      <div>
        <StyledTable className="table table-bordered">
          <StyledHeader className="thead-light">
            <StyledRow>
              {columns.columns.map((column: ColumnDefinition) => (
                <TableHeaderCell column={column} totalWidth={totalWidth} />
              ))}
            </StyledRow>
          </StyledHeader>
          <StyledBody>
            {data.map((lostunits: LostUnits) => (
              <TableRow
                columns={columns}
                lostUnits={lostunits}
                totalWidth={totalWidth}
              />
            ))}
          </StyledBody>
        </StyledTable>
        <ReactPaginate
          nextLabel="next >"
          pageRangeDisplayed={5}
          marginPagesDisplayed={2}
          forcePage={selectedPageIndex - 1}
          pageCount={totalPages}
          onPageChange={handlePageClick}
          previousLabel="< previous"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakLabel="..."
          breakClassName="page-item"
          breakLinkClassName="page-link"
          containerClassName="pagination"
          activeClassName="active"
        />
      </div>
    );
  } else {
    return (
      <StyledTable className="table table-bordered">
        <StyledHeader className="thead-light">
          <StyledRow>
            {columns.columns.map((column: ColumnDefinition) => (
              <TableHeaderCell column={column} totalWidth={totalWidth} />
            ))}
          </StyledRow>
        </StyledHeader>
        <StyledBody>
          {data.map((lostunits: LostUnits) => (
            <TableRow
              columns={columns}
              lostUnits={lostunits}
              totalWidth={totalWidth}
            />
          ))}
        </StyledBody>
      </StyledTable>
    );
  }
};

/*
    <td>{lostunits.dsku}</td>
    <td>{lostunits.warehouseId}</td>
    <td>{lostunits.shipmentId}</td>
    <td>{lostunits.asnId}</td>
    <td>{lostunits.qty}</td>
    <td>{lostunits.reason}</td>
    <td width="10px">{lostunits.notes}</td>
    <td>{lostunits.sellerid}</td>
    <td>{lostunits.reportedBy}</td>
    <td>{lostunits.asanaTicket ? <a href={lostunits.asanaTicket} target="_blank">link</a> : null}</td>
    <td>{lostunits.zendeskTicket ? <a href={`https://flexport-fulfillment.zendesk.com/agent/tickets/${lostunits.zendeskTicket}`} target="_blank">{lostunits.zendeskTicket}</a> : null}</td>
    <td>{(moment(lostunits.lostAt)).format("YYYY-MM-DD")}</td>
    <td>{moment(lostunits.created).format("YYYY-MM-DD")}</td>
    <td>{moment(lostunits.updated).format("YYYY-MM-DD")}</td>
    <td>
      <Button type="button" onClick={() => onDelete(lostunits.id || 0)}>
        Delete
      </Button>
    </td>
  </StyledRow>
  */
